// Domain-owned route containers. Do not import legacy connected-container or all-domain registries here.
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import type { EvidenceItem, WorkflowCommand } from "@swot/swot-components";
import { readCmtsSession } from "../../auth/authStore.js";
import { resolveCurrentCmtsSession } from "../../session/cmtsSessionResolver.js";
import { useCmtsDispatch } from "@store/hooks.js";
import { authActions } from "@store/authSlice.js";
import { permissionActions } from "@store/permissionSlice.js";
import { tenantActions } from "@store/tenantSlice.js";
import { serializeReduxError } from "@store/thunkExtra.js";
import { useCmtsAuth } from "../../auth/authHooks.js";
import { useComplaintRegister } from "@hooks/useComplaintRegister.js";
import { useComplaintDetail } from "@hooks/useComplaintDetail.js";
import { useComplaintWorkflow } from "@hooks/useComplaintWorkflow.js";
import { useEvidence } from "@hooks/useEvidence.js";
import { cmtsKeys } from "@hooks/cmtsQueryKeys.js";
import type { RepositoryResult } from "../../repositories/repositoryResult.js";
import type { CmtsHostRuntime } from "../../../../app/appRuntime.js";
import type { CmtsComplaintDetailVm, CmtsComplaintRecordVm } from "../../view-models/complaintViewModels.js";
import type { CmtsConfigurationDraftVm, CmtsConfigurationVersionVm } from "../../mappers/configurationMappers.js";
import { queryObjectFromSearch } from "../../filters/cmtsFilterEngine.js";
import { decideCmtsExport, createCmtsExportAuditPayload } from "../../export/cmtsSecureExport.js";
import { getCmtsPersonaLandingPath } from "../../routes/cmtsPersonaLanding.js";
import { normalizeTypedReportCode, reportCodeFromPathname } from "../../reporting/cmtsReportConfig.js";
import { throwCmtsCanonicalError } from "../../api/cmtsApiErrors.js";
import { cmtsMasterDataEntityForPathSegment } from "../../master-data/cmtsEnterpriseMasterDataRegistry.js";
import { activeRows, assertRepositoryOk, businessValueOptionOf, complaintQuery, dataOr, diagnosticsOf, emptyBackendDashboard, errorOf, invalidateAdminArea, loadMasterOptions, optionOf, tenantId, useAdminDependencyData, useCmtsRouterSearch, type CmtsConnectedContainerProps } from "../domainContainerSupport.js";
import { CmtsComplaintRegisterPage } from "@modules/pages/cmtsComplaintRegisterPage.js";
import { CmtsComplaintCreatePage } from "@modules/pages/cmtsComplaintCreatePage.js";
import { CmtsComplaintDetailPage } from "@modules/pages/cmtsComplaintDetailPage.js";
import { CmtsWorkflowPage } from "@modules/pages/cmtsWorkflowPage.js";
import { CmtsEvidencePage } from "@modules/pages/cmtsEvidencePage.js";
import { CmtsSlaPage } from "@modules/pages/cmtsSlaPage.js";
import { CmtsEscalationPage } from "@modules/pages/cmtsEscalationPage.js";
import { CmtsAuditLogsPage } from "@modules/pages/cmtsAuditLogsPage.js";
import { runtimeErrorMessage } from "../runtimeContainerErrors.js";
import { extractItems } from "../../repositories/repositoryCollection.js";

function useComplaintId(): string | undefined {
  return useParams<{ complaintId: string }>().complaintId;
}

function repositoryRecordRows(
  result: RepositoryResult<unknown> | undefined,
): readonly Record<string, unknown>[] {
  const payload = result?.ok ? result.data : [];
  return extractItems(payload).filter(
    (row): row is Record<string, unknown> =>
      typeof row === "object" &&
      row !== null &&
      !Array.isArray(row),
  );
}

function useConnectedComplaint(runtime: CmtsHostRuntime) {
  const id = useComplaintId();
  const t = tenantId(runtime);
  const detail = useComplaintDetail(runtime.repositories.complaints, id, t);
  const workflow = useComplaintWorkflow(runtime.repositories.workflow, id, t);
  const executeCommand = async (command: WorkflowCommand) => {
    await workflow.execute.mutateAsync(command);
    const reloadComplaintDetail = detail.refetch; await reloadComplaintDetail();
  };
  const record = dataOr<CmtsComplaintDetailVm | null>(detail.data, null);
  const allowed = dataOr(workflow.allowedActions.data, []);
  const mergedRecord = record ? { ...record, allowedActions: allowed } : record;
  return { id, tenantId: t, detail, workflow, record: mergedRecord, diagnostics: diagnosticsOf(runtime.diagnostics, detail.data, workflow.allowedActions.data), executeCommand };
}

export function CmtsAuditLogsContainer({ runtime }: CmtsConnectedContainerProps) {
  const t = tenantId(runtime);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["cmts", "audit", t], queryFn: () => runtime.repositories.audit.logs({ tenantId: t }), retry: 1 });
  const exportHistory = useQuery({ queryKey: ["cmts", "audit-export-history", t], queryFn: () => runtime.repositories.audit.exportHistory(t), retry: 1 });
  const exportAudit = useMutation({ mutationFn: (rows: readonly Record<string, unknown>[]) => runtime.repositories.audit.exportAudit({ tenantId: t, rowCount: rows.length }, t), onSuccess: async () => queryClient.invalidateQueries({ queryKey: ["cmts", "audit-export-history", t] }) });
  return <CmtsAuditLogsPage events={dataOr(query.data, []) as readonly Record<string, unknown>[]} exportHistory={dataOr(exportHistory.data, []) as readonly Record<string, unknown>[]} diagnostics={diagnosticsOf(runtime.diagnostics, query.data, exportHistory.data, exportAudit.data)} loading={query.isLoading || exportHistory.isLoading || exportAudit.isPending} error={runtimeErrorMessage(query.error) ?? runtimeErrorMessage(exportHistory.error) ?? runtimeErrorMessage(exportAudit.error) ?? null} onOpenEvent={async (row) => dataOr(await runtime.repositories.audit.detail(String(row.id ?? row.code ?? ""), t), row) as Record<string, unknown>} onExportAudit={async (rows) => { await exportAudit.mutateAsync(rows as readonly Record<string, unknown>[]); }} onOpenLinkedRecord={(row) => { const id = String(row.entityId ?? row.recordId ?? row.complaintId ?? ""); if (id && String(row.entityType ?? "").toUpperCase().includes("COMPLAINT")) navigate(`/cmts/complaints/${encodeURIComponent(id)}`); }} />;
}

export function CmtsComplaintCreateContainer({ runtime, permissionContext }: CmtsConnectedContainerProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const t = tenantId(runtime);
  const referenceQuery = useQuery({ queryKey: ["cmts", "complaint-create-master-options", t], queryFn: async () => {
    const [source, category, priority, severity, subcategory, departments, customers, suppliers, products, processes, users, confidentiality] = await Promise.all([
      runtime.repositories.masterData.dropdown("complaint-source", t),
      runtime.repositories.masterData.dropdown("complaint-category", t),
      runtime.repositories.masterData.dropdown("priority", t),
      runtime.repositories.masterData.dropdown("severity", t),
      runtime.repositories.masterData.dropdown("complaint-subcategory", t),
      runtime.repositories.masterData.dropdown("department", t),
      runtime.repositories.reference.organizationTree(t),
      runtime.repositories.reference.organizationTree(t),
      runtime.repositories.masterData.dropdown("product", t),
      runtime.repositories.masterData.dropdown("process", t),
      runtime.repositories.reference.users(t),
      runtime.repositories.masterData.dropdown("confidentiality", t),
    ]);
    const rowsFrom = <T,>(result: RepositoryResult<T> | undefined): readonly Record<string, unknown>[] => {
      if (!result?.ok || !Array.isArray(result.data)) return [];
      return result.data.filter((row): row is Record<string, unknown> => typeof row === "object" && row !== null && !Array.isArray(row));
    };
    const organizationRows = rowsFrom(customers);
    return {
      sourceChannel: rowsFrom(source).map((row) => businessValueOptionOf(row, "complaint-source")),
      categoryId: rowsFrom(category).map((row) => optionOf(row, "Category")),
      priority: rowsFrom(priority).map((row) => businessValueOptionOf(row, "priority")),
      severity: rowsFrom(severity).map((row) => businessValueOptionOf(row, "severity")),
      subcategoryId: rowsFrom(subcategory).map((row) => optionOf(row, "Subcategory")),
      departmentId: rowsFrom(departments).map((row) => optionOf(row, "Department")),
      customerId: organizationRows.filter((row) => String(row.type ?? row.organizationType ?? row.partyType ?? "").toLowerCase().includes("customer") || String(row.id ?? "").toLowerCase().includes("customer")).map((row) => optionOf(row, "Customer")),
      supplierId: rowsFrom(suppliers).filter((row) => String(row.type ?? row.organizationType ?? row.partyType ?? "").toLowerCase().includes("supplier") || String(row.id ?? "").toLowerCase().includes("supplier")).map((row) => optionOf(row, "Supplier")),
      productProcessReferenceId: [...rowsFrom(products), ...rowsFrom(processes)].map((row) => optionOf(row, "Product / Process")),
      ownerId: rowsFrom(users).map((row) => optionOf(row, "User")),
      confidentiality: rowsFrom(confidentiality).map((row) => businessValueOptionOf(row, "confidentiality")),
    };
  }, retry: 1 });
  const create = useMutation({
    mutationFn: (payload: Record<string, unknown>) => {
      const normalizedPayload: Record<string, unknown> = { ...payload, tenantId: t };
      if (!normalizedPayload.categoryId && normalizedPayload.category) normalizedPayload.categoryId = normalizedPayload.category;
      return runtime.repositories.complaints.create(normalizedPayload);
    },
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: cmtsKeys.complaints(tenantId(runtime)) }),
        queryClient.invalidateQueries({ queryKey: ["cmts", "dashboard"] }),
        queryClient.invalidateQueries({ queryKey: ["cmts", "reports", tenantId(runtime)] }),
        queryClient.invalidateQueries({ queryKey: cmtsKeys.tasks(tenantId(runtime)) }),
        queryClient.invalidateQueries({ queryKey: cmtsKeys.notifications(tenantId(runtime)) }),
        queryClient.invalidateQueries({ queryKey: ["cmts", "rca-queue", tenantId(runtime)] }),
        queryClient.invalidateQueries({ queryKey: ["cmts", "capa-queue", tenantId(runtime)] }),
        queryClient.invalidateQueries({ queryKey: ["cmts", "rca"] }),
        queryClient.invalidateQueries({ queryKey: ["cmts", "capa"] }),
      ]);
      if (result.ok) navigate(`/cmts/complaints/${encodeURIComponent(result.data.id)}`);
    },
  });
  return <CmtsComplaintCreatePage diagnostics={runtime.diagnostics} permissionContext={permissionContext} masterOptions={referenceQuery.data ?? {}} busy={create.isPending} error={runtimeErrorMessage(create.error) ?? errorOf(create.data)} onCreate={async (payload) => { assertRepositoryOk(await create.mutateAsync(payload), "Complaint create"); }} onExportDraft={async (payload) => {
    const resolved = resolveCurrentCmtsSession();
    if (resolved.status !== "ready") throwCmtsCanonicalError({ code: "CMTS_ERR_MISSING_ACTOR", message: resolved.reason ?? "Session is required before complaint draft export.", details: { action: "complaint-draft-export" } });
    const session = resolved.session;
    const columns = Object.keys(payload.draft);
    const decision = decideCmtsExport(session, columns);
    if (!decision.allowed) throwCmtsCanonicalError({ code: "CMTS_ERR_SOD_BLOCKED", message: decision.reason ?? "Complaint draft export is not allowed for this session.", details: { action: "complaint-draft-export" } });
    const auditPayload = createCmtsExportAuditPayload(session, "Complaint Create Draft", { draftOnly: true }, 1);
    assertRepositoryOk(await runtime.repositories.legacyReports.export({ tenantId: t, exportType: payload.exportType, visibleColumns: decision.visibleColumns, draft: payload.draft, validationDiagnostics: payload.validationDiagnostics, audit: auditPayload }), "Complaint draft export");
  }} />;
}

export function CmtsComplaintDetailContainer({ runtime, permissionContext }: CmtsConnectedContainerProps) {
  const connected = useConnectedComplaint(runtime);
  const editMasterOptions = useQuery({
    queryKey: ["cmts", "complaint-detail-master-options", connected.tenantId],
    queryFn: async () => {
      const [sourceChannel, priority, severity, confidentiality] = await Promise.all([
        loadMasterOptions(runtime, "complaint-source", connected.tenantId, "complaint-source"),
        loadMasterOptions(runtime, "priority", connected.tenantId, "priority"),
        loadMasterOptions(runtime, "severity", connected.tenantId, "severity"),
        loadMasterOptions(runtime, "confidentiality", connected.tenantId, "confidentiality"),
      ]);
      return { sourceChannel, priority, severity, confidentiality };
    },
    retry: 1,
  });
  const queryClient = useQueryClient();
  const update = useMutation({
    mutationFn: (payload: Record<string, unknown>) => runtime.repositories.complaints.update(connected.id ?? "", payload),
    onSuccess: async () => {
      await connected.detail.refetch();
      await queryClient.invalidateQueries({ queryKey: cmtsKeys.complaints(tenantId(runtime)) });
    },
  });
  return <CmtsComplaintDetailPage record={connected.record} masterOptions={editMasterOptions.data ?? {}} permissionContext={permissionContext} actorId={readCmtsSession()?.userId ?? "current-user"} actorName={readCmtsSession()?.displayName} diagnostics={connected.diagnostics} onCommand={connected.executeCommand} onUpdate={async (payload) => { await update.mutateAsync(payload); }} />;
}

export function CmtsComplaintRegisterContainer({ permissionContext }: CmtsConnectedContainerProps) {
  const navigate = useNavigate();
  return <CmtsComplaintRegisterPage
    permissionContext={permissionContext}
    onSelect={(record) => navigate(`/cmts/complaints/${encodeURIComponent(record.id)}`)}
    onOpenWorkflow={(record) => navigate(`/cmts/complaints/${encodeURIComponent(record.id)}/workflow`)}
    onOpenEvidence={(record) => navigate(`/cmts/complaints/${encodeURIComponent(record.id)}/evidence`)}
  />;
}

/*
 * complaintsRouteContainers.tsx
 * Replace only the existing CmtsEscalationContainer function with this block.
 * This block expects the existing repositoryRecordRows helper in the same file.
 */

export function CmtsEscalationContainer({ runtime }: CmtsConnectedContainerProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const t = tenantId(runtime);
  const queue = useQuery({ queryKey: ["cmts", "escalations", t], queryFn: () => runtime.repositories.escalation.queue(t), retry: 1 });
  const alerts = useQuery({ queryKey: ["cmts", "alerts", t], queryFn: () => runtime.repositories.alerts.my(t), retry: 1 });
  const escalationTypeOptions = useQuery({
    queryKey: ["cmts", "reference-options", "ESCALATION_TRIGGER_TYPE", t],
    queryFn: async () => {
      const result = await runtime.repositories.reference.values({
        tenantId: t,
        typeCode: "ESCALATION_TRIGGER_TYPE",
      });
      const options = repositoryRecordRows(result)
        .map((row) => ({
          value: String(row.code ?? row.value ?? row.id ?? "").trim(),
          label: String(row.name ?? row.label ?? row.displayName ?? row.code ?? "").trim(),
        }))
        .filter((option) => option.value && option.label);
      return options.length > 0
        ? options
        : [
            { value: "SLA_AT_RISK", label: "SLA at risk" },
            { value: "SLA_BREACHED", label: "SLA breached" },
            { value: "MANUAL", label: "Manual escalation" },
            { value: "REOPENED", label: "Complaint reopened" },
          ];
    },
    retry: 1,
  });
  const ownerOptions = useQuery({ queryKey: ["cmts", "reference-users", "escalation", t], queryFn: async () => {
    const result = await runtime.repositories.reference.users(t);
    const rows = repositoryRecordRows(result);
    return rows.map((row) => ({ value: String(row.id ?? row.userId ?? row.value ?? "").trim(), label: String(row.fullName ?? row.displayName ?? row.label ?? row.email ?? row.name ?? "").trim() })).filter((option) => option.value && option.label);
  }, retry: 1 });
  const complaintOptionsQuery = useQuery({ queryKey: ["cmts", "escalation-complaints", t], queryFn: () => runtime.repositories.complaints.list({ tenantId: t, pageNumber: 1, pageSize: 200 }), retry: 1 });
  const ack = useMutation({ mutationFn: (row: Record<string, unknown>) => runtime.repositories.escalation.acknowledge(String(row.id ?? row.escalationId ?? ""), t), onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ["cmts", "alerts", t] }); await queryClient.invalidateQueries({ queryKey: ["cmts", "escalations", t] }); await queryClient.invalidateQueries({ queryKey: ["cmts", "dashboards"] }); } });
  const resolve = useMutation({ mutationFn: (row: Record<string, unknown>) => runtime.repositories.escalation.resolve(String(row.id ?? row.escalationId ?? ""), t), onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ["cmts", "alerts", t] }); await queryClient.invalidateQueries({ queryKey: ["cmts", "escalations", t] }); await queryClient.invalidateQueries({ queryKey: ["cmts", "dashboards"] }); } });
  const create = useMutation({ mutationFn: (row: Record<string, unknown>) => runtime.repositories.escalation.create(row, t), onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ["cmts", "alerts", t] }); await queryClient.invalidateQueries({ queryKey: ["cmts", "escalations", t] }); await queryClient.invalidateQueries({ queryKey: cmtsKeys.tasks(t) }); await queryClient.invalidateQueries({ queryKey: cmtsKeys.notifications(t) }); await queryClient.invalidateQueries({ queryKey: ["cmts", "calendar", t] }); await queryClient.invalidateQueries({ queryKey: ["cmts", "dashboards"] }); } });
  const update = useMutation({ mutationFn: ({ row, payload }: { row: Record<string, unknown>; payload: Record<string, unknown> }) => runtime.repositories.escalation.update(String(row.id ?? row.escalationId ?? row.alertId ?? ""), payload, t), onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ["cmts", "alerts", t] }); await queryClient.invalidateQueries({ queryKey: ["cmts", "escalations", t] }); await queryClient.invalidateQueries({ queryKey: ["cmts", "dashboards"] }); } });
  const queueRows = extractItems(queue.data?.ok ? queue.data.data : undefined) as readonly Record<string, unknown>[];
  const alertRows = extractItems(alerts.data?.ok ? alerts.data.data : undefined) as readonly Record<string, unknown>[];
  const escalationComplaintRows = dataOr<readonly Record<string, unknown>[]>(complaintOptionsQuery.data, []);
  const escalationComplaintOptions = escalationComplaintRows.map((row) => ({ value: String(row.id ?? row.complaintId ?? ""), label: String(row.complaintNumber ?? row.complaintCode ?? row.code ?? row.title ?? "Complaint") })).filter((option) => option.value);
  const ownerNamesById = new Map((ownerOptions.data ?? []).map((option) => [option.value.toLowerCase(), option.label] as const));
  const complaintLabelsById = new Map(escalationComplaintOptions.map((option) => [option.value.toLowerCase(), option.label] as const));
  const rows = (queueRows.length ? queueRows : alertRows).map((row, index) => {
    const complaintId = String(row.complaintId ?? row.linkedEntityId ?? "").trim();
    const ownerId = String(row.ownerId ?? row.recipientUserId ?? row.targetUserId ?? "").trim();
    return {
      ...row,
      id: String(row.id ?? row.escalationId ?? row.alertId ?? `escalation-${index}`),
      complaintId,
      complaintNumber: row.complaintNumber ?? row.complaintCode ?? complaintLabelsById.get(complaintId.toLowerCase()) ?? "-",
      ownerId,
      ownerName: row.ownerName ?? row.recipientUserName ?? ownerNamesById.get(ownerId.toLowerCase()) ?? (ownerId ? "Assigned user" : "Unassigned"),
      status: String(row.status ?? "OPEN").toUpperCase(),
      priority: String(row.priority ?? row.severity ?? "HIGH").toUpperCase(),
    };
  });
  return <CmtsEscalationPage queue={rows} reminders={alertRows.slice(0, 6)} masterOptions={{ escalationType: escalationTypeOptions.data ?? [], complaints: escalationComplaintOptions, owners: ownerOptions.data ?? [], levels: [{ value: "1", label: "Level 1" }, { value: "2", label: "Level 2" }, { value: "3", label: "Level 3" }] }} diagnostics={diagnosticsOf(runtime.diagnostics, queue.data, alerts.data, ack.data, resolve.data, create.data, update.data)} onOpenEscalation={(row) => { const id = String(row.complaintId ?? row.linkedEntityId ?? ""); if (id) navigate(`/cmts/complaints/${encodeURIComponent(id)}/workflow`); }} onAcknowledge={async (row) => { await ack.mutateAsync(row); }} onResolve={async (row) => { await resolve.mutateAsync(row); }} onEscalate={async (row) => { const currentLevel = Number(String(row.escalationLevel ?? row.levelNumber ?? "1").replace(/\D/g, "")) || 1; await create.mutateAsync({ ...row, escalationLevel: String(Math.min(currentLevel + 1, 10)), reason: `Escalated further: ${String(row.reason ?? row.summary ?? row.title ?? "Management attention required")}` }); }} onCreateEscalation={async (payload) => { await create.mutateAsync(payload); }} onUpdateEscalation={async (row, payload) => { await update.mutateAsync({ row, payload }); }} />;
}

export function CmtsEvidenceContainer({ runtime }: CmtsConnectedContainerProps) {
  const id = useComplaintId();
  const t = tenantId(runtime);
  const evidence = useEvidence(runtime.repositories.evidence, id, t);
  const result = evidence.list.data;
  const items = result?.ok ? result.data : ([] as readonly EvidenceItem[]);
  const fileTypeOptions = useQuery({ queryKey: ["cmts", "master-options", "evidence-type", t], queryFn: () => loadMasterOptions(runtime, "evidence-type", t, "evidence-type"), retry: 1 });
  const linkedEntityTypeOptions = useQuery({ queryKey: ["cmts", "master-options", "evidence-linked-entity", t], queryFn: () => loadMasterOptions(runtime, "evidence-linked-entity", t, "evidence-linked-entity"), retry: 1 });
  const confidentialityOptions = useQuery({ queryKey: ["cmts", "master-options", "evidence-confidentiality", t], queryFn: () => loadMasterOptions(runtime, "confidentiality", t, "confidentiality"), retry: 1 });
  const queryClient = useQueryClient();
  const addMetadata = useMutation({
    mutationFn: (payload: Record<string, unknown>) => runtime.repositories.evidence.addMetadata(id ?? "", { ...payload, tenantId: t }, t),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: cmtsKeys.evidence(id ?? "", t) }); },
  });
  const deleteEvidence = useMutation({
    mutationFn: (item: EvidenceItem) => runtime.repositories.evidence.deleteForComplaint(id ?? "", String((item as Record<string, unknown>).id ?? ""), t),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: cmtsKeys.evidence(id ?? "", t) }); },
  });
  return <CmtsEvidencePage items={items} masterOptions={{ fileType: fileTypeOptions.data ?? [], linkedEntityType: linkedEntityTypeOptions.data ?? [], confidentiality: confidentialityOptions.data ?? [] }} diagnostics={diagnosticsOf(runtime.diagnostics, result, addMetadata.data, deleteEvidence.data)} onUpload={async (payload) => { await addMetadata.mutateAsync(payload ?? {}); }} onDownload={async (item) => { await evidence.download(String((item as Record<string, unknown>).id ?? "")); }} onDelete={async (item) => { await deleteEvidence.mutateAsync(item); }} />;
}

export function CmtsSlaContainer({ runtime }: CmtsConnectedContainerProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const t = tenantId(runtime);
  const complaints = useComplaintRegister(
    runtime.repositories.complaints,
    {
      tenantId: t,
      pageNumber: 1,
      pageSize: 250,
      sortBy: "slaDueDate",
      sortDirection: "ASC",
    },
  );

  const trackers = useQuery({
    queryKey: ["cmts", "sla-trackers", t],
    queryFn: () => runtime.repositories.sla.trackers(t),
    retry: 1,
  });

  const profiles = useQuery({
    queryKey: ["cmts", "sla-profiles", t],
    queryFn: () => runtime.repositories.sla.profiles(t),
    retry: 1,
  });

  const profileRows = repositoryRecordRows(profiles.data);
  const profileIds = profileRows
    .map((row) => String(row.id ?? row.profileId ?? "").trim())
    .filter(Boolean);

  const rules = useQuery({
    queryKey: ["cmts", "sla-rules", t, profileIds.join(",")],
    enabled: profileIds.length > 0,
    queryFn: async () => {
      const results = await Promise.all(
        profileIds.map((profileId) => runtime.repositories.sla.rules(profileId, true)),
      );
      return results.flatMap((result) =>
        repositoryRecordRows(result),
      );
    },
    retry: 1,
  });

  const owners = useQuery({
    queryKey: ["cmts", "sla-owner-options", t],
    queryFn: () => runtime.repositories.reference.users(t),
    retry: 1,
  });

  const ownerDropdown = useQuery({
    queryKey: ["cmts", "sla-owner-dropdown", t],
    queryFn: () => runtime.repositories.masterData.dropdown("user", t),
    retry: 1,
  });

  const priorities = useQuery({
    queryKey: ["cmts", "sla-priority-options", t],
    queryFn: () => loadMasterOptions(runtime, "priority", t, "priority"),
    retry: 1,
  });

  const refreshSla = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["cmts", "sla-trackers", t] }),
      queryClient.invalidateQueries({ queryKey: cmtsKeys.complaints(t) }),
      queryClient.invalidateQueries({ queryKey: ["cmts", "dashboards"] }),
    ]);
  };

  const createEscalation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      runtime.repositories.escalation.create(payload, t),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["cmts", "escalations", t] }),
        queryClient.invalidateQueries({ queryKey: cmtsKeys.tasks(t) }),
        queryClient.invalidateQueries({ queryKey: cmtsKeys.notifications(t) }),
        queryClient.invalidateQueries({ queryKey: ["cmts", "dashboards"] }),
      ]);
    },
  });

  const createSla = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      runtime.repositories.sla.create(payload, t),
    onSuccess: refreshSla,
  });

  const updateSla = useMutation({
    mutationFn: ({ row, payload }: {
      row: Record<string, unknown>;
      payload: Record<string, unknown>;
    }) =>
      runtime.repositories.sla.update(
        String(row.id ?? row.slaTrackerId ?? ""),
        payload,
        t,
      ),
    onSuccess: refreshSla,
  });

  const pauseSla = useMutation({
    mutationFn: (row: Record<string, unknown>) =>
      runtime.repositories.sla.pause(
        String(row.id ?? row.slaTrackerId ?? ""),
        { reason: "Paused from SLA tracker" },
        t,
      ),
    onSuccess: refreshSla,
  });

  const resumeSla = useMutation({
    mutationFn: (row: Record<string, unknown>) =>
      runtime.repositories.sla.resume(
        String(row.id ?? row.slaTrackerId ?? ""),
        { reason: "Resumed from SLA tracker" },
        t,
      ),
    onSuccess: refreshSla,
  });

  const recalculateSla = useMutation({
    mutationFn: () => runtime.repositories.sla.evaluate({}, t),
    onSuccess: refreshSla,
  });

  const complaintRows = dataOr<readonly CmtsComplaintRecordVm[]>(complaints.data, []);
  const trackerRows = dataOr(trackers.data, []) as readonly Record<string, unknown>[];
  const ruleRows = rules.data ?? [];
  const ownerRows = [
    ...repositoryRecordRows(owners.data),
    ...repositoryRecordRows(ownerDropdown.data),
  ];

  const profilesById = new Map<string, Record<string, unknown>>(
    profileRows.map((row) => [
      String(row.id ?? row.profileId ?? "").trim().toLowerCase(),
      row,
    ] as const),
  );

  const rulesById = new Map<string, Record<string, unknown>>(
    ruleRows.map((row) => [
      String(row.id ?? row.ruleId ?? "").trim().toLowerCase(),
      row,
    ] as const),
  );

  const complaintOptions = complaintRows.map((row) => ({
    value: row.id,
    label: `${row.complaintNumber} — ${row.title}`,
  }));

  const ruleOptions = ruleRows
    .map((row) => {
      const value = String(row.id ?? row.ruleId ?? "").trim();
      const profileId = String(row.slaProfileId ?? row.profileId ?? "")
        .trim()
        .toLowerCase();
      const profile = profilesById.get(profileId);
      const profileLabel = String(
        profile?.name ?? profile?.code ?? row.profileCode ?? "SLA profile",
      ).trim();
      const responseHours = String(row.responseHours ?? "").trim();
      const resolutionHours = String(row.resolutionHours ?? "").trim();
      const durationLabel = [
        responseHours ? `${responseHours}h response` : "",
        resolutionHours ? `${resolutionHours}h resolution` : "",
      ].filter(Boolean).join(" / ");
      return {
        value,
        label: String(
          row.ruleName ??
            row.name ??
            row.code ??
            `${profileLabel}${durationLabel ? ` — ${durationLabel}` : ""}`,
        ).trim(),
      };
    })
    .filter((option) => option.value && option.label);

  const complaintsById = new Map<string, Record<string, unknown>>(
    complaintRows.map((row) => [
      String(row.id).toLowerCase(),
      row as unknown as Record<string, unknown>,
    ] as const),
  );

  const ownerNamesById = new Map<string, string>(
    ownerRows
      .map((row) => [
        String(row.id ?? row.userId ?? row.value ?? "").trim().toLowerCase(),
        String(
          row.fullName ??
            row.displayName ??
            row.label ??
            row.userName ??
            row.userCode ??
            row.email ??
            row.name ??
            "",
        ).trim(),
      ] as const)
      .filter(([id, label]) => Boolean(id && label)),
  );

  const priorityNamesById = new Map<string, string>(
    (priorities.data ?? [])
      .map((option) => [option.value.toLowerCase(), option.label] as const),
  );

  const resolvedTrackerRows = trackerRows.map((tracker) => {
    const complaintId = String(
      tracker.complaintId ?? tracker.linkedEntityId ?? "",
    ).trim();
    const complaint = complaintsById.get(complaintId.toLowerCase());
    const slaRuleId = String(tracker.slaRuleId ?? tracker.ruleId ?? "")
      .trim()
      .toLowerCase();
    const slaRule = rulesById.get(slaRuleId);
    const ownerId = String(
      tracker.ownerId ??
        tracker.ownerUserId ??
        complaint?.ownerId ??
        complaint?.complaintOwnerId ??
        "",
    ).trim();
    const priorityId = String(
      tracker.priorityId ??
        complaint?.priorityId ??
        slaRule?.priorityId ??
        "",
    ).trim();
    const candidatePriority = String(
      tracker.priorityName ??
        tracker.priorityCode ??
        tracker.priority ??
        complaint?.priorityName ??
        complaint?.priorityCode ??
        priorityNamesById.get(priorityId.toLowerCase()) ??
        complaint?.priority ??
        "",
    ).trim();
    const priority = /^(unknown|unassigned|not specified)$/i.test(candidatePriority)
      ? ""
      : candidatePriority;
    const paused = tracker.paused === true || tracker.isPaused === true;
    const breached = tracker.breach === true || tracker.isBreached === true;
    const nearBreach = tracker.nearBreach === true || tracker.isNearBreach === true;
    const candidateSlaStatus = String(
      tracker.slaStatus ??
        tracker.status ??
        tracker.slaStateCode ??
        complaint?.slaStatus ??
        "",
    ).trim();
    const backendSlaStatus = /^(not calculated|unknown)$/i.test(candidateSlaStatus)
      ? ""
      : candidateSlaStatus;

    const resolvedOwnerName = String(
      tracker.ownerName ??
        tracker.ownerDisplayName ??
        ownerNamesById.get(ownerId.toLowerCase()) ??
        complaint?.ownerName ??
        complaint?.ownerDisplayName ??
        complaint?.owner ??
        "",
    ).trim();
    const ownerName = /^(unknown|unassigned|not specified)$/i.test(resolvedOwnerName)
      ? ownerId
        ? "Assigned user"
        : "Unassigned"
      : resolvedOwnerName || (ownerId ? "Assigned user" : "Unassigned");

    return {
      ...tracker,
      complaintId,
      complaintNumber:
        tracker.complaintNumber ??
        complaint?.complaintNumber ??
        complaint?.complaintCode,
      title: tracker.title ?? complaint?.title ?? "SLA Tracker",
      ownerId,
      ownerName,
      priority,
      priorityCode: priority,
      slaStatus: paused
        ? "PAUSED"
        : breached
          ? "BREACHED"
          : nearBreach
            ? "NEAR_BREACH"
            : backendSlaStatus || "WITHIN_SLA",
      dueDate: tracker.dueDate ?? tracker.resolutionDueAt,
    };
  });

  const escalate = async (row: Record<string, unknown>) => {
    await createEscalation.mutateAsync({
      complaintId: row.complaintId,
      escalationType: "SLA_BREACHED",
      escalationLevel: "Level 1",
      ownerId: row.ownerId,
      priority: row.priorityCode ?? row.priority,
      reason: `SLA escalation: ${String(
        row.title ?? row.complaintNumber ?? "SLA item",
      )}`,
    });
  };

  return (
    <CmtsSlaPage
      masterOptions={{ complaint: complaintOptions, slaType: ruleOptions }}
      diagnostics={diagnosticsOf(
        runtime.diagnostics,
        complaints.data,
        trackers.data,
        profiles.data,
        createEscalation.data,
        createSla.data,
        updateSla.data,
        pauseSla.data,
        resumeSla.data,
        recalculateSla.data,
      )}
      slaRows={resolvedTrackerRows}
      onOpenComplaint={(row) => {
        const id = String(row.complaintId ?? row.linkedEntityId ?? "");
        if (id) navigate(`/cmts/complaints/${encodeURIComponent(id)}/workflow`);
      }}
      onEscalate={escalate}
      onCreateTracker={async (payload) => {
        await createSla.mutateAsync(payload);
      }}
      onUpdateTracker={async (row, payload) => {
        await updateSla.mutateAsync({ row, payload });
      }}
      onPauseSla={async (row) => {
        await pauseSla.mutateAsync(row);
      }}
      onResumeSla={async (row) => {
        await resumeSla.mutateAsync(row);
      }}
      onRecalculateSla={async () => {
        await recalculateSla.mutateAsync();
      }}
    />
  );
}

export function CmtsWorkflowContainer({ runtime, permissionContext }: CmtsConnectedContainerProps) {
  const connected = useConnectedComplaint(runtime);
  return <CmtsWorkflowPage complaintId={connected.id ?? ""} status={connected.record?.status ?? "backend-driven"} actions={connected.record?.allowedActions ?? []} actorId="current-user" permissionContext={permissionContext} diagnostics={connected.diagnostics} onCommand={connected.executeCommand} />;
}